/**
 * Shared visual primitives: stroke text, the HAND EGG logo, hype rays, manifest-backed art (heads, gloves,
 * special icons), key caps / button hints, stat pips and the menu backdrop.
 */
import './components.css';
import type { ComponentChildren, CSSProperties } from 'preact';
import { CHARACTER_IDS, characterById, RARITY_NAMES, specialInfo, type CharacterId, type Rarity } from '@hf/shared';
import type { MenuAction } from '@hf/client-core/input/types';
import { thousands } from '@hf/client-core/ui-model/format';
import { ballSkinKey, gloveKey, headKeys, iconKey, packKey, resolveImage } from './assets';
import { inputStyle } from './nav';

type Style = CSSProperties;

/** Outlined, extruded type (Luckiest Guy / Bungee) with a gradient or flat fill. */
export function Stroke({ text, class: cls = '', style }: { text: string; class?: string; style?: Style }) {
  return (
    <span class={`hf-stroke ${cls}`} style={style} data-text={text}>
      <span class="hf-stroke__front">{text}</span>
    </span>
  );
}

/** The two-line logo: chrome-blue HAND over gold EGG (design: UI / Logo — HAND EGG). */
export function Logo({ size = 'lg', edition = false }: { size?: 'lg' | 'sm'; edition?: boolean }) {
  return (
    <div class={`hf-logo ${size}`} role="img" aria-label="Hand Egg">
      <Stroke text="HAND" class="t-display hf-logo__head" />
      <Stroke text="EGG" class="t-display hf-logo__foot" />
      {edition ? (
        <div class="hf-logo__edition t-display">
          <span aria-hidden="true">☆</span> SUNDAY ARCADE EDITION <span aria-hidden="true">☆</span>
        </div>
      ) : null}
    </div>
  );
}

export function Rays({
  a,
  b,
  rays = 18,
  x = '50%',
  y = '50%',
  speed = 60,
  reverse = false,
  vignette = true,
  class: cls = '',
  style,
}: {
  a: string;
  b: string;
  rays?: number;
  x?: string;
  y?: string;
  speed?: number;
  reverse?: boolean;
  vignette?: boolean;
  class?: string;
  style?: Style;
}) {
  const vars = {
    '--ray-a': a,
    '--ray-b': b,
    '--ray-n': `${180 / rays}deg`,
    '--ray-x': x,
    '--ray-y': y,
    '--ray-speed': `${speed}s`,
    ...style,
  } as Style;
  return (
    <div
      class={`hf-rays ${reverse ? 'reverse' : ''} ${vignette ? '' : 'no-vignette'} ${cls}`}
      style={vars}
      aria-hidden="true"
    />
  );
}

/** Any manifest image (first key that exists); renders `fallback` when none does. */
export function Art({
  keys,
  alt = '',
  class: cls = '',
  flip = false,
  style,
  fallback = null,
}: {
  keys: readonly string[];
  alt?: string;
  class?: string;
  flip?: boolean;
  style?: Style;
  fallback?: ComponentChildren;
}) {
  const a = resolveImage(keys);
  if (!a) return <>{fallback}</>;
  const img = (
    <img
      class={`hf-art ${flip ? 'flip' : ''} ${cls}`}
      src={a.url}
      width={a.width}
      height={a.height}
      alt={alt}
      draggable={false}
      decoding="async"
      style={style}
    />
  );
  if (!a.fallback || !a.url.endsWith('.webp')) return img;
  return (
    <picture style={{ display: 'contents' }}>
      <source srcset={a.url} type="image/webp" />
      <img
        class={`hf-art ${flip ? 'flip' : ''} ${cls}`}
        src={a.fallback}
        width={a.width}
        height={a.height}
        alt={alt}
        draggable={false}
        decoding="async"
        style={style}
      />
    </picture>
  );
}

const INITIALS: Record<CharacterId, string> = {
  rookie: 'R',
  bigtex: 'T',
  zorg: 'Z',
  grandpa: 'G',
  turbo: 'U',
  dome: 'D',
  rumble: 'M',
  hopper: 'H',
  lodestone: 'L',
};

/** A character head. `flip` mirrors it to face left (AWAY side). */
export function Head({
  character,
  expression = 'neutral',
  flip = false,
  class: cls = '',
  style,
}: {
  character: CharacterId;
  expression?: string;
  flip?: boolean;
  class?: string;
  style?: Style;
}) {
  const def = characterById(character);
  return (
    <Art
      keys={headKeys(character, expression)}
      alt={def.name}
      flip={flip}
      class={`hf-head ${cls}`}
      style={style}
      fallback={
        <div
          class={`hf-art-fallback hf-head ${cls}`}
          style={{ ...style, background: `radial-gradient(circle at 35% 30%, ${def.accent}, #3a2f5d 70%)` }}
          aria-label={def.name}
        >
          <span style={{ fontSize: '2.2em' }}>{INITIALS[character]}</span>
        </div>
      }
    />
  );
}

/** The floating glove (home = red, away = blue). Away gloves face left in the art already for ready/reach. */
export function Glove({
  side,
  pose = 'ready',
  flip = false,
  class: cls = '',
  style,
}: {
  side: 'home' | 'away';
  pose?: 'open' | 'fist' | 'grip' | 'ready' | 'reach';
  flip?: boolean;
  class?: string;
  style?: Style;
}) {
  return (
    <Art
      keys={[gloveKey(side, pose), gloveKey(side, 'ready'), gloveKey(side, 'open')]}
      flip={flip}
      class={`hf-glove ${cls}`}
      style={style}
    />
  );
}

/** Special icon (icon/<special key>) with a lightning glyph fallback. */
export function SpecialIcon({ special, class: cls = '' }: { special: number; class?: string }) {
  const info = specialInfo(special as never);
  return (
    <Art
      keys={[iconKey(info.key)]}
      alt={info.name}
      class={`hf-special-icon ${cls}`}
      fallback={
        <svg class={`hf-special-icon ${cls}`} viewBox="0 0 24 24" aria-label={info.name}>
          <path d="M13 2 4 14h7l-1 8 9-12h-7z" fill="currentColor" />
        </svg>
      }
    />
  );
}

/** A ball-skin icon (Store BALLS tab, design/exports/store-balls.png). */
export function BallSkinArt({ skin, class: cls = '' }: { skin: string; class?: string }) {
  return (
    <Art
      keys={[ballSkinKey(skin)]}
      alt={skin}
      class={`hf-ball-art ${cls}`}
      fallback={<div class={`hf-art-fallback hf-ball-art ${cls}`} aria-hidden="true" />}
    />
  );
}

/** A coin-pack icon (Store COINS tab, design/exports/store.png). */
export function PackArt({ pack, class: cls = '' }: { pack: string; class?: string }) {
  return (
    <Art
      keys={[packKey(pack)]}
      alt={pack}
      class={`hf-pack-art ${cls}`}
      fallback={<div class={`hf-art-fallback hf-pack-art ${cls}`} aria-hidden="true" />}
    />
  );
}

/** The coin icon (balance pill, price chips). */
export function CoinIcon({ class: cls = '' }: { class?: string }) {
  return (
    <Art
      keys={[iconKey('coin')]}
      alt=""
      class={`hf-coin-icon ${cls}`}
      fallback={
        <span class={`hf-coin-icon hf-coin-icon--fallback ${cls}`} aria-hidden="true">
          ●
        </span>
      }
    />
  );
}

/** Top-right wallet balance pill, every Store / character-select mockup: coin icon + amount, with an optional
 *  "GET COINS" ⊕ button (design/exports/select.png, store.png). */
export function CoinBalance({
  balance,
  onGetCoins,
  class: cls = '',
}: {
  balance: number;
  onGetCoins?: () => void;
  class?: string;
}) {
  return (
    <div class={`hf-coin-balance ${cls}`}>
      <CoinIcon class="hf-coin-balance__icon" />
      <span class="hf-coin-balance__amount t-display">{thousands(balance)}</span>
      {onGetCoins ? (
        <button class="hf-coin-balance__add interactive" onClick={onGetCoins} aria-label="Get coins">
          +
        </button>
      ) : null}
    </div>
  );
}

/** A coin price chip (locked character / ball-skin card): coin icon + price, rarity-coloured. */
export function PriceChip({
  price,
  rarity,
  class: cls = '',
}: {
  price: number;
  rarity?: Rarity;
  class?: string;
}) {
  return (
    <span class={`hf-price-chip t-display ${rarity !== undefined ? `hf-rarity-${rarity}` : ''} ${cls}`}>
      <CoinIcon class="hf-price-chip__icon" />
      {thousands(price)}
    </span>
  );
}

/** A small rarity-coloured tag (COMMON / RARE / EPIC / LEGENDARY), design/exports/store-balls.png. */
export function RarityTag({ rarity, class: cls = '' }: { rarity: Rarity; class?: string }) {
  return <span class={`hf-rarity-tag hf-rarity-${rarity} t-broadcast ${cls}`}>{RARITY_NAMES[rarity]}</span>;
}

/** Lock badge on an unowned character / ball-skin card. */
export function LockBadge({ class: cls = '' }: { class?: string }) {
  return (
    <span class={`hf-lock-badge ${cls}`} aria-hidden="true">
      <svg viewBox="0 0 24 24">
        <path
          d="M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5Zm-3 8V6a3 3 0 0 1 6 0v3Z"
          fill="currentColor"
        />
      </svg>
    </span>
  );
}

export const preloadKeysFor = (characters: readonly CharacterId[] = CHARACTER_IDS): string[] =>
  characters.flatMap((c) => [
    ...headKeys(c, 'neutral').slice(0, 1),
    ...headKeys(c, 'determined').slice(0, 1),
    ...headKeys(c, 'celebrating').slice(0, 1),
    iconKey(specialInfo(characterById(c).signature).key),
  ]);

const KEY_DPAD: Record<string, 'left' | 'right' | 'up' | 'down'> = {
  'D←': 'left',
  'D→': 'right',
  'D↑': 'up',
  'D↓': 'down',
};

/**
 * Key cap. A controller D-pad direction ("D↓", which read as "D+" at HUD size) is drawn as a D-pad with that arm
 * lit, the same glyph Football 101 uses.
 */
export function Key({ label, pad = false }: { label: string; pad?: boolean }) {
  const dir = pad ? KEY_DPAD[label] : undefined;
  if (dir) {
    const arm = (d: typeof dir, x: number, y: number, w: number, h: number) => (
      <rect x={x} y={y} width={w} height={h} rx="2.5" class={d === dir ? 'on' : ''} />
    );
    return (
      <span class="hf-key pad dpad" aria-label={`D-pad ${dir}`}>
        <svg viewBox="0 0 32 32" aria-hidden="true">
          {arm('up', 11, 2, 10, 11)}
          {arm('down', 11, 19, 10, 11)}
          {arm('left', 2, 11, 11, 10)}
          {arm('right', 19, 11, 11, 10)}
          <rect x="11" y="11" width="10" height="10" />
        </svg>
      </span>
    );
  }
  return <span class={`hf-key ${pad ? 'pad' : ''}`}>{label}</span>;
}

const KB_LABEL: Record<MenuAction, string> = {
  confirm: 'ENTER',
  back: 'ESC',
  up: '▲',
  down: '▼',
  left: '◀',
  right: '▶',
  pause: 'P',
};
const PAD_LABEL: Record<MenuAction, string> = {
  confirm: 'A',
  back: 'B',
  up: '▲',
  down: '▼',
  left: '◀',
  right: '▶',
  pause: 'START',
};

/** Key cap for a menu action on the last used device family (ENTER/ESC on keyboards, A/B on pads). */
export function ActionKey({ action }: { action: MenuAction }) {
  const pad = inputStyle.value === 'pad';
  if (inputStyle.value === 'touch') return null;
  return <Key label={(pad ? PAD_LABEL : KB_LABEL)[action]} pad={pad} />;
}

/** Bottom-of-screen button hints; labels follow the last used device (keys vs pad buttons). */
export function Hints({
  items,
  class: cls = '',
}: {
  items: readonly [MenuAction | string, string][];
  class?: string;
}) {
  const pad = inputStyle.value === 'pad';
  if (inputStyle.value === 'touch') return null;
  return (
    <div class={`hf-hints ${cls}`}>
      {items.map(([action, label], idx) => {
        const keys = action.split('|').map((a) => {
          const k = a as MenuAction;
          return (pad ? PAD_LABEL[k] : KB_LABEL[k]) ?? a;
        });
        return (
          <span class="hf-hint" key={`${idx}:${action}:${label}`}>
            {keys.map((k, ki) => (
              <Key key={`${ki}:${k}`} label={k} pad={pad && /^[AB]$|START/.test(k)} />
            ))}
            <span>{label}</span>
          </span>
        );
      })}
    </div>
  );
}

export function Pips({ value, max = 5 }: { value: number; max?: number }) {
  return (
    <div class="hf-pips" role="meter" aria-valuenow={value} aria-valuemin={0} aria-valuemax={max}>
      {Array.from({ length: max }, (_, i) => (
        <span key={i} class={`hf-pip ${i < value ? 'on' : ''}`} />
      ))}
    </div>
  );
}

/** Menu backdrop: blurred painted stadium (if the manifest has one) + dim + hype rays. */
export function Backdrop({
  rays,
  stadium = 'day',
  children,
}: {
  rays?: { a: string; b: string; rays?: number; x?: string; y?: string; speed?: number };
  stadium?: 'day' | 'night' | 'none';
  children?: ComponentChildren;
}) {
  const art =
    stadium === 'none'
      ? null
      : resolveImage(
          stadium === 'day'
            ? ['stadium/day', 'stadium/backdrop-day', 'stadium/bg', 'stadium/stands']
            : ['stadium/night', 'stadium/backdrop-night', 'stadium/bg', 'stadium/stands'],
        );
  return (
    <div class="hf-fill" aria-hidden="true">
      {art ? <div class="hf-backdrop" style={{ backgroundImage: `url(${art.url})` }} /> : null}
      <div class="hf-dim" />
      {rays ? <Rays {...rays} style={{ opacity: 0.35 }} /> : null}
      {children}
    </div>
  );
}

/** Broadcast ticker: a gold label and an endlessly scrolling line ("HFN SCORES"). */
export function Ticker({
  label,
  items,
  secondsPerItem = 7,
  class: cls = '',
}: {
  label: string;
  items: readonly ComponentChildren[];
  secondsPerItem?: number;
  class?: string;
}) {
  return (
    <div class={`hf-ticker ${cls}`}>
      <div class="hf-ticker__label">{label}</div>
      <div class="hf-ticker__track">
        <div
          class="hf-ticker__run"
          style={{ '--ticker-duration': `${Math.max(12, items.length * secondsPerItem)}s` } as Style}
        >
          {items.map((it, i) => (
            <span key={i}>
              {it}
              <span aria-hidden="true" style={{ color: 'var(--gold)', marginLeft: 'calc(48 * var(--u))' }}>
                ★
              </span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
